import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, TrendingUp, Info, FileText, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'tds_calculator_data';

const incomeTypes = [
  { value: 'salary', label: 'Salary (192)', rate: 0 },
  { value: 'interest', label: 'Interest on Securities (193)', rate: 10 },
  { value: 'dividend', label: 'Dividend (194)', rate: 10 },
  { value: 'contractor', label: 'Contractor/Professional (194C/J)', rate: 10 },
  { value: 'rent', label: 'Rent (194I)', rate: 10 },
  { value: 'commission', label: 'Commission/Brokerage (194H)', rate: 5 },
  { value: 'fd', label: 'Fixed Deposit Interest (194A)', rate: 10 },
  { value: 'lottery', label: 'Lottery/Winnings (194B)', rate: 30 },
  { value: 'freelance', label: 'Freelance/Fees (194J)', rate: 10 }
];

export const TDSCalculator = () => {
  const [incomeType, setIncomeType] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).incomeType : 'salary';
  });
  
  const [amount, setAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).amount : 100000;
  });
  
  const [tdsRate, setTdsRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).tdsRate : 10;
  });
  
  const [hasPan, setHasPan] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).hasPan : 'yes';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      incomeType,
      amount,
      tdsRate,
      hasPan
    }));
  }, [incomeType, amount, tdsRate, hasPan]);

  useEffect(() => {
    // Update TDS rate when income type changes
    const selectedType = incomeTypes.find(type => type.value === incomeType);
    if (selectedType) {
      setTdsRate(selectedType.rate);
    }
  }, [incomeType]);

  const calculate = () => {
    const grossAmount = parseFloat(amount) || 0;
    let rate = parseFloat(tdsRate) || 0;

    // If no PAN, TDS rate is 20% or the applicable rate, whichever is higher
    if (hasPan === 'no') {
      rate = Math.max(rate, 20);
    }

    const tdsAmount = (grossAmount * rate) / 100;
    const netAmount = grossAmount - tdsAmount;

    // Calculate excess TDS if applicable
    let excessTds = 0;
    let isExcessTds = false;
    
    // For interest income, if below threshold ($40,000 for senior citizens, $10,000 for others)
    if (incomeType === 'interest' || incomeType === 'fd') {
      if (grossAmount < 40000 && tdsAmount > 0) {
        isExcessTds = true;
        excessTds = tdsAmount;
      }
    }

    return {
      grossAmount,
      tdsRate: rate,
      tdsAmount,
      netAmount,
      excessTds,
      isExcessTds,
      effectiveRate: rate,
      panPenalty: hasPan === 'no' ? rate - (parseFloat(tdsRate) || 0) : 0
    };
  };

  const generateBreakdownData = () => {
    const results = calculate();
    
    return [
      { name: 'Net Amount', value: results.netAmount },
      { name: 'TDS Deducted', value: results.tdsAmount }
    ];
  };

  const generateGrossNetComparison = () => {
    const results = calculate();
    
    return [
      { category: 'Gross Amount', amount: results.grossAmount },
      { category: 'TDS Amount', amount: results.tdsAmount },
      { category: 'Net Receivable', amount: results.netAmount }
    ];
  };

  const generateRateImpactData = () => {
    const grossAmount = parseFloat(amount) || 0;
    const rates = [5, 10, 15, 20, 30];
    
    return rates.map(rate => {
      const tds = (grossAmount * rate) / 100;
      const net = grossAmount - tds;
      
      return {
        rate: `${rate}%`,
        tdsAmount: tds,
        netAmount: net
      };
    });
  };

  const results = calculate();
  const breakdownData = generateBreakdownData();
  const comparisonData = generateGrossNetComparison();
  const rateImpactData = generateRateImpactData();

  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-3xl font-semibold text-foreground">
              TDS Calculator
            </h1>
          </div>
          <p className="text-muted-foreground">Calculate Tax Deducted at Source on various income types</p>
        </div>

        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is TDS?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  TDS (Tax Deducted at Source) is a system where tax is collected at the point of income generation. 
                  The payer deducts tax before making payment, which is deposited with the government on your behalf.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter transaction details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="incomeType" className="text-sm font-medium">
                      Income Type
                    </Label>
                    <Select value={incomeType} onValueChange={setIncomeType}>
                      <SelectTrigger id="incomeType">
                        <SelectValue placeholder="Select income type" />
                      </SelectTrigger>
                      <SelectContent>
                        {incomeTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="amount" className="text-sm font-medium">
                      Amount ($)
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="100000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Gross payment amount</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tdsRate" className="text-sm font-medium">
                      TDS Rate (%)
                    </Label>
                    <Input
                      id="tdsRate"
                      type="number"
                      value={tdsRate}
                      onChange={(e) => setTdsRate(e.target.value)}
                      placeholder="10"
                      step="0.1"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Applicable TDS percentage</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hasPan" className="text-sm font-medium">
                      PAN Card Status
                    </Label>
                    <Select value={hasPan} onValueChange={setHasPan}>
                      <SelectTrigger id="hasPan">
                        <SelectValue placeholder="Select PAN status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">PAN Available</SelectItem>
                        <SelectItem value="no">No PAN</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {hasPan === 'no' ? 'TDS will be 20% or applicable rate (whichever is higher)' : 'Normal TDS rate applies'}
                    </p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setAmount(amount)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">TDS Calculation Results</CardTitle>
                    <CardDescription>Your TDS breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Gross Amount</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.grossAmount)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">TDS Amount</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(results.tdsAmount)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{results.effectiveRate}% TDS rate</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Net Receivable</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.netAmount)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Effective Rate</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {results.effectiveRate.toFixed(2)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {hasPan === 'no' ? 'Higher rate (No PAN)' : 'Standard rate'}
                        </p>
                      </div>
                    </div>

                    {hasPan === 'no' && results.panPenalty > 0 && (
                      <>
                        <Separator className="my-6" />
                        <div className="p-6 rounded-xl bg-destructive/10 border border-destructive/20">
                          <h4 className="font-semibold text-destructive mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            No PAN Penalty
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            Without PAN card, you're paying an additional {results.panPenalty.toFixed(2)}% TDS, 
                            resulting in extra deduction of:
                          </p>
                          <p className="text-2xl font-display font-bold text-destructive">
                            {formatCurrency((results.grossAmount * results.panPenalty) / 100)}
                          </p>
                        </div>
                      </>
                    )}

                    {results.isExcessTds && (
                      <>
                        <Separator className="my-6" />
                        <div className="p-6 rounded-xl bg-secondary/10 border border-secondary/20">
                          <h4 className="font-semibold text-secondary mb-3 flex items-center gap-2">
                            <Info className="w-5 h-5" />
                            Refund Eligible
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            Your income is below the threshold for TDS. You can claim a refund of:
                          </p>
                          <p className="text-2xl font-display font-bold text-secondary">
                            {formatCurrency(results.excessTds)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            File Form 15G/15H to avoid TDS deduction or claim refund during ITR filing
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Gross vs Net Amount</CardTitle>
                <CardDescription className="text-base">
                  See the impact of TDS on your income
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="category" 
                        stroke="hsl(var(--muted-foreground))"
                        angle={-15}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="amount" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Amount Breakdown</CardTitle>
                  <CardDescription>
                    TDS vs Net amount split
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={breakdownData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {breakdownData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">TDS Rate Impact</CardTitle>
                  <CardDescription>
                    How different rates affect your income
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={rateImpactData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="rate" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="tdsAmount" fill="hsl(var(--destructive))" name="TDS Amount" />
                        <Bar dataKey="netAmount" fill="hsl(var(--secondary))" name="Net Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding TDS</CardTitle>
                <CardDescription className="text-base">
                  Learn how TDS works and how to manage it effectively
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    How TDS Affects Your Cash Flow
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    From your gross amount of <strong className="text-primary">{formatCurrency(results.grossAmount)}</strong>, 
                    TDS of <strong className="text-primary">{formatCurrency(results.tdsAmount)}</strong> ({results.effectiveRate}%) 
                    is deducted. You receive only <strong>{formatCurrency(results.netAmount)}</strong> in hand.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="mb-2">Key points about TDS:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>TDS is advance tax paid on your behalf</li>
                      <li>It's credited to your PAN and shown in Form 26AS</li>
                      <li>You can claim refund if TDS exceeds actual tax liability</li>
                      <li>TDS reduces your final tax payment at year-end</li>
                    </ul>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Refund Eligibility Explanation
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    TDS is deducted at a flat rate, but your actual tax liability depends on your total income and applicable tax slabs.
                  </p>
                  
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ When You Get Refund</h4>
                      <p className="text-muted-foreground">
                        If your total income is below taxable limit ($3 lakh in new regime), all TDS deducted is refundable. 
                        File ITR to claim refund.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Excess TDS Scenarios</h4>
                      <p className="text-muted-foreground">
                        Interest income below $40,000, senior citizens with low income, or freelancers with high expenses 
                        often have excess TDS that's refundable.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Common TDS Mistakes
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-destructive/10 border-l-4 border-destructive">
                      <h4 className="font-semibold text-foreground mb-2">❌ Not Providing PAN</h4>
                      <p className="text-muted-foreground">
                        Without PAN, TDS is deducted at 20% (or higher). Always provide your PAN to avoid extra deduction. 
                        {hasPan === 'no' && ` You're losing ${formatCurrency((results.grossAmount * results.panPenalty) / 100)} extra!`}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-destructive/10 border-l-4 border-destructive">
                      <h4 className="font-semibold text-foreground mb-2">❌ Ignoring Form 15G/15H</h4>
                      <p className="text-muted-foreground">
                        If your income is below taxable limit, submit Form 15G (below 60 years) or 15H (senior citizens) 
                        to banks/payers to avoid TDS deduction.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-destructive/10 border-l-4 border-destructive">
                      <h4 className="font-semibold text-foreground mb-2">❌ Not Filing ITR</h4>
                      <p className="text-muted-foreground">
                        Even if income is low, file ITR to claim TDS refund. Refunds can only be claimed through ITR filing.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important TDS Rules
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Threshold Limits:</strong> TDS is not applicable below certain 
                        thresholds (e.g., $10,000 for interest from savings account, $5,000 for interest from FD per quarter).
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Section-wise Rates:</strong> Different TDS rates apply to different 
                        income types. Always verify the correct section and rate.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">TDS Certificate:</strong> Always collect TDS certificates (Form 16A) 
                        from payers. These are required for ITR filing.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Lower Deduction Certificate:</strong> If expected tax is lower, 
                        apply for lower/nil TDS certificate from Income Tax Department.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Compliance Tips
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">📋 Track Your TDS</h4>
                      <p className="text-muted-foreground">
                        Check Form 26AS regularly on the Income Tax portal to verify all TDS credits. Mismatch in TDS 
                        can delay refunds.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">📋 Keep Records</h4>
                      <p className="text-muted-foreground">
                        Maintain all payment proofs, TDS certificates, and transaction records. These are needed for 
                        ITR filing and refund claims.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">📋 File on Time</h4>
                      <p className="text-muted-foreground">
                        File ITR before the deadline (31st July for individuals). Late filing can result in penalties 
                        and delayed refunds.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TDSCalculator;
